import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search, User, ChevronDown } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const Header = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Логування для діагностики
  useEffect(() => {
  }, [user, isAuthenticated, isAdmin]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/discounts?search=${searchQuery}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {/* Фіксований хедер */}
      <header className="fixed top-0 left-0 w-full bg-white shadow-md z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Логотип */}
            <Link to="/" className="flex items-center">
              <img
                src="/logo.png"
                alt="ПрофБонус"
                className="h-10 w-10 object-contain mr-2"
                loading="eager"
                fetchpriority="high"
                decoding="sync"
              />
              <span className="text-xl font-bold text-primary-700">ПрофБонус</span>
            </Link>

            {/* Пошук (desktop) */}
            <div className="hidden md:block flex-grow max-w-md mx-4">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  placeholder="Пошук знижок..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 pr-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button type="submit" className="absolute left-3 top-2.5 text-gray-400">
                  <Search size={18} />
                </button>
              </form>
            </div>

            {/* Навігація (desktop) */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-secondary-800 hover:text-primary-600 font-medium">
                Головна
              </Link>
              <Link to="/discounts" className="text-secondary-800 hover:text-primary-600 font-medium">
                Каталог знижок
              </Link>

              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={toggleProfileMenu}
                    className="flex items-center text-secondary-800 hover:text-primary-600 font-medium focus:outline-none"
                  >
                    <div className="relative w-8 h-8 rounded-full overflow-hidden bg-gray-200 mr-2">
                      {user?.profilePictureUrl ? (
                        <img
                          src={user.profilePictureUrl}
                          alt={user.fullName}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User size={16} className="text-primary-600" />
                        </div>
                      )}
                    </div>
                    <span className="mr-1">{user?.fullName?.split(' ')[0]}</span>
                    <ChevronDown size={16} />
                  </button>

                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        Профіль користувача
                      </Link>
                      <Link
                        to="/usage-history"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        Історія використання
                      </Link>

                      {isAdmin && (
                        <Link
                          to="/admin/dashboard"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          Адмін-панель
                        </Link>
                      )}

                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Вийти
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition"
                >
                  Увійти
                </Link>
              )}
            </nav>

            {/* Кнопка мобільного меню */}
            <button
              onClick={toggleMenu}
              className="md:hidden text-secondary-800 focus:outline-none"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Мобільне меню */}
          {isMenuOpen && (
            <div className="md:hidden mt-4">
              <div className="py-2">
                <form onSubmit={handleSearch} className="relative">
                  <input
                    type="text"
                    placeholder="Пошук знижок..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pl-10 pr-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button type="submit" className="absolute left-3 top-2.5 text-gray-400">
                    <Search size={18} />
                  </button>
                </form>
              </div>

              <nav className="flex flex-col space-y-3 mt-4">
                <Link
                  to="/"
                  className="text-secondary-800 py-2 hover:text-primary-600 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Головна
                </Link>
                <Link
                  to="/discounts"
                  className="text-secondary-800 py-2 hover:text-primary-600 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Каталог знижок
                </Link>

                {isAuthenticated ? (
                  <>
                    <Link
                      to="/profile"
                      className="text-secondary-800 py-2 hover:text-primary-600 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Профіль
                    </Link>
                    <Link
                      to="/usage-history"
                      className="text-secondary-800 py-2 hover:text-primary-600 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Історія використання
                    </Link>

                    {isAdmin && (
                      <Link
                        to="/admin/dashboard"
                        className="text-secondary-800 py-2 hover:text-primary-600 font-medium"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Адмін-панель
                      </Link>
                    )}

                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="text-left text-secondary-800 py-2 hover:text-primary-600 font-medium"
                    >
                      Вийти
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition inline-block text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Увійти
                  </Link>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Основний контент з відступом під фіксований хедер */}
      <main className="pt-16">
        {/* Тут буде рендеритись контент сторінок */}
      </main>
    </>
  );
};

export default Header;
